/**
 * Money. The single source of truth for monetary values in KickBack Pro.
 *
 * Rules this module exists to enforce (see docs/adr/0003-money-representation.md):
 *  - Money is an integer count of minor units (cents), carried as `bigint`.
 *  - No `number` arithmetic on money, ever. `number` appears in exactly two
 *    places in this file: the wire codec boundary and basis-point weights
 *    (which are dimensionless integers, not money).
 *  - Splits are exact: allocating an amount across parties loses no cent and
 *    gains no cent, and the allocation is deterministic.
 */
export const CURRENCIES = ['USD'];
export class MoneyError extends Error {
    name = 'MoneyError';
}
/** Largest value representable losslessly as a JSON number: ~$90 trillion. */
export const MAX_WIRE_CENTS = BigInt(Number.MAX_SAFE_INTEGER);
export function money(cents, currency = 'USD') {
    if (typeof cents !== 'bigint') {
        throw new MoneyError(`money() requires a bigint, received ${typeof cents}`);
    }
    return { cents, currency };
}
export function zero(currency = 'USD') {
    return money(0n, currency);
}
function sameCurrency(a, b) {
    if (a.currency !== b.currency) {
        throw new MoneyError(`currency mismatch: ${a.currency} vs ${b.currency}`);
    }
    return a.currency;
}
export function add(a, b) {
    return money(a.cents + b.cents, sameCurrency(a, b));
}
export function subtract(a, b) {
    return money(a.cents - b.cents, sameCurrency(a, b));
}
export function negate(a) {
    return money(-a.cents, a.currency);
}
export function absolute(a) {
    return money(a.cents < 0n ? -a.cents : a.cents, a.currency);
}
export function sum(amounts, currency = 'USD') {
    return amounts.reduce((acc, m) => add(acc, m), zero(currency));
}
export function isZero(a) {
    return a.cents === 0n;
}
export function isNegative(a) {
    return a.cents < 0n;
}
/** -1 | 0 | 1, comparable with the same semantics as Array#sort comparators. */
export function compare(a, b) {
    sameCurrency(a, b);
    if (a.cents < b.cents)
        return -1;
    if (a.cents > b.cents)
        return 1;
    return 0;
}
export function equals(a, b) {
    return a.currency === b.currency && a.cents === b.cents;
}
/**
 * Split `total` across `weights` using largest-remainder allocation.
 *
 * Guarantees:
 *  - `sum(result) === total` exactly, for any weights and any sign of total.
 *  - Deterministic: ties in the remainder go to the lowest index.
 *  - Sign-symmetric: allocate(-x, w) === allocate(x, w).map(negate).
 *    This matters because a clawback must reverse a split exactly, cent for
 *    cent, or the reversal leaves dust in the ledger.
 */
export function allocate(total, weights) {
    if (weights.length === 0) {
        throw new MoneyError('allocate() requires at least one weight');
    }
    if (weights.some((w) => w < 0n)) {
        throw new MoneyError('allocate() weights must be non-negative');
    }
    const totalWeight = weights.reduce((acc, w) => acc + w, 0n);
    if (totalWeight === 0n) {
        throw new MoneyError('allocate() weights must sum to more than zero');
    }
    const negative = total.cents < 0n;
    const magnitude = negative ? -total.cents : total.cents;
    const bases = [];
    const remainders = [];
    let distributed = 0n;
    for (const weight of weights) {
        const scaled = magnitude * weight;
        const base = scaled / totalWeight;
        bases.push(base);
        remainders.push(scaled % totalWeight);
        distributed += base;
    }
    let leftover = magnitude - distributed;
    // `leftover` is strictly less than weights.length, so this terminates fast.
    while (leftover > 0n) {
        let best = -1;
        let bestRemainder = -1n;
        for (let i = 0; i < remainders.length; i += 1) {
            const r = remainders[i] ?? 0n;
            if (r > bestRemainder) {
                bestRemainder = r;
                best = i;
            }
        }
        if (best === -1)
            break;
        bases[best] = (bases[best] ?? 0n) + 1n;
        remainders[best] = -1n;
        leftover -= 1n;
    }
    return bases.map((c) => money(negative ? -c : c, total.currency));
}
/* ------------------------------------------------------------------ *
 * Split terms — the product's differentiator, modeled as a first-class
 * value rather than a calculation performed at attribution time.
 * ------------------------------------------------------------------ */
export const BASIS_POINTS_TOTAL = 10_000;
export function assertValidSplitTerms(terms) {
    const parts = [terms.platformBps, terms.creatorBps, terms.memberBps];
    for (const part of parts) {
        if (!Number.isInteger(part) || part < 0) {
            throw new MoneyError(`split basis points must be non-negative integers, got ${part}`);
        }
    }
    const total = parts.reduce((a, b) => a + b, 0);
    if (total !== BASIS_POINTS_TOTAL) {
        throw new MoneyError(`split basis points must sum to ${BASIS_POINTS_TOTAL}, got ${total}`);
    }
}
/**
 * Apply split terms to a gross commission amount. Exact by construction:
 * platform + creator + member === commission, always.
 *
 * Ordering note: platform first, then creator, then member. Remainder cents
 * therefore favour the platform on ties. That is a deliberate, documented
 * choice — see the ADR — because the alternative (favouring the member) makes
 * the platform's revenue non-deterministic against the same inputs.
 */
export function applySplit(commission, terms) {
    assertValidSplitTerms(terms);
    const [platform, creator, member] = allocate(commission, [
        BigInt(terms.platformBps),
        BigInt(terms.creatorBps),
        BigInt(terms.memberBps),
    ]);
    return {
        platform: platform ?? zero(commission.currency),
        creator: creator ?? zero(commission.currency),
        member: member ?? zero(commission.currency),
    };
}
export function toWire(m) {
    const magnitude = m.cents < 0n ? -m.cents : m.cents;
    if (magnitude > MAX_WIRE_CENTS) {
        throw new MoneyError(`amount ${m.cents} exceeds the losslessly serializable range (${MAX_WIRE_CENTS} cents)`);
    }
    return { amountCents: Number(m.cents), currency: m.currency };
}
export function fromWire(wire) {
    if (!Number.isSafeInteger(wire.amountCents)) {
        throw new MoneyError(`amountCents must be a safe integer, received ${wire.amountCents}`);
    }
    if (!CURRENCIES.includes(wire.currency)) {
        throw new MoneyError(`unsupported currency ${wire.currency}`);
    }
    return money(BigInt(wire.amountCents), wire.currency);
}
/** Postgres BIGINT arrives from `pg` as a string. This is the DB-side codec. */
export function fromDb(value, currency = 'USD') {
    return money(typeof value === 'bigint' ? value : BigInt(value), currency);
}
export function toDb(m) {
    return m.cents.toString();
}
/**
 * Display formatting. This is the one place a formatted string is allowed,
 * and it is never used to build an API response body.
 */
export function formatForDisplay(m, locale = 'en-US') {
    const negative = m.cents < 0n;
    const magnitude = negative ? -m.cents : m.cents;
    const whole = magnitude / 100n;
    const fraction = (magnitude % 100n).toString().padStart(2, '0');
    /**
     * `.toString()`, and not because it reads better.
     *
     * `Intl.NumberFormat.prototype.format` takes a BigInt in V8 and in JSC, so
     * passing `whole` directly works in Node, in the test suite, in the web app
     * and in every browser. **Hermes does not.** It coerces its argument, and a
     * BigInt throws `TypeError: Cannot convert BigInt to number` — which reached
     * a device as a crash on the first screen that renders money, and reached it
     * with a green build behind it.
     *
     * Converted explicitly, the way `toWire` above already converts. Only the
     * *whole* part goes through `Number`, and only to be grouped with separators:
     * the fractional part is computed from the BigInt above and never touches a
     * float, so no rounding of cents is possible here.
     *
     * The conversion is lossy above 2^53 whole currency units — nine quadrillion
     * dollars — which is not a balance this system will hold, and would be a
     * grouping artefact rather than a wrong amount if it ever did.
     */
    const grouped = new Intl.NumberFormat(locale).format(Number(whole));
    const symbol = m.currency === 'USD' ? '$' : `${m.currency} `;
    return `${negative ? '-' : ''}${symbol}${grouped}.${fraction}`;
}
//# sourceMappingURL=money.js.map